<template>
  <button
    class="row attachment-list-row-component-container"
    @click="rowClick"
  >
    <!--
        Using a div here to take advantage of background position/size.
        We could/should use an img and the object-fit and object-position properties to do the same but it is not supported in IE11.
      -->
    <bookmark-button
      class="bookmark-button"
      :asset-name="rowAttachment.assetTitle"
      :bookmarkable-type="'Attachment'"
      :bookmarkable-id="rowAttachment.id"
      :source="bookmarkSource"
    />
    <div
      v-if="thumbnail"
      class="thumbnail thumbnail-img"
      role="img"
      :aria-label="`Media for ${rowAttachment.assetTitle}`"
      :style="{ backgroundImage: thumbnail }"
    >
      <attachment-progress
        v-if="attachmentState"
        :duration="rowAttachment.metadata.duration"
        :watched="attachmentState.currentTimestamp"
      />
    </div>
    <div
      v-else
      class="thumbnail"
    >
      <icon
        :name="rowAttachment.icon"
        variant="767676"
      />
      <attachment-progress
        v-if="attachmentState"
        :duration="rowAttachment.metadata.duration"
        :watched="attachmentState.currentTimestamp"
      />
    </div>
    <div class="name">
      <lock-button
        v-if="locked"
        :resource="{ type: 'attachment', id: rowAttachment.id }"
        :source="{ type: 'Locked Attachment', value: rowAttachment.id }"
      />
      <purchase-order-preview
        v-if="user.previewer"
        :resource="rowAttachment"
      />
      <slot
        v-if="hasSlot"
        name="name"
      />
      <template v-else>
        {{ name }}
      </template>
    </div>
    <div class="action-container">
      <div
        v-if="rowAttachment.duration"
        :class="['duration', {'in-progress': inProgress}]"
        v-html="durationText"
      />
      <div class="view">
        <template v-if="showPlay">
          <icon
            v-if="inProgress"
            name="play-filled"
            variant="mastery"
          />
          <icon
            v-else
            name="play-outline"
            variant="mastery"
          />
        </template>
        <p v-else>
          View
        </p>
      </div>
    </div>
  </button>
</template>

<script>
import AttachmentProgress from '@components/ui/attachment-progress';
import BookmarkButton from '@components/singletons/bookmarkButton';
import Icon from '@components/singletons/icon';
import LockButton from '@components/singletons/lock-button';
import PurchaseOrderPreview from '@components/singletons/purchaseOrderPreview';

import Attachment from '@/classes/attachment';

export default {
  name: 'AttachmentList',
  components: { AttachmentProgress, BookmarkButton, Icon, LockButton, PurchaseOrderPreview },
  props: {
    attachment: {
      type: Object,
    },
    card: {
      type: Object,
    },
    source: {
      type: Object,
      default: () => ({}),
    },
    showLock: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    attachmentState() {
      if (this.rowAttachment) {
        return this.$store.getters['attachmentStates/get'][this.rowAttachment.id];
      }
    },
    bookmarkSource() {
      if (this.card) {
        return { type: 'Card', id: this.card.id };
      } else {
        return this.source;
      }
    },
    completed() {
      if (this.attachmentState) {
        return this.rowAttachment.completedState(this.attachmentState);
      }
    },
    displayAttachment() {
      if (this.rowAttachment) {
        if (this.rowAttachment.displayAttachmentId) {
          return this.$store.getters['attachments/get'][this.rowAttachment.displayAttachmentId];
        }
        return this.rowAttachment;
      }
    },
    durationText() {
      if (this.inProgress) {
        return `${this.formatDuration(this.timeRemaining)} <span class='remaining-text'>Remaining</span>`;
      }
      return this.rowAttachment.duration;
    },
    hasSlot() {
      return !!this.$slots.name || this.$scopedSlots.name?.();
    },
    inProgress() {
      if (this.attachmentState) {
        return this.timeRemaining > 0 && !this.completed;
      }
    },
    name() {
      if (this.card) {
        return this.card.title;
      } else {
        return this.rowAttachment.assetTitle;
      }
    },
    rowAttachment() {
      if (this.attachment) {
        return this.attachment;
      } else if (this.card) {
        return this.$store.getters['attachments/get'][this.card.assetId];
      }
    },
    locked() {
      if (this.card || !this.showLock) {
        return false;
      }

      return this.rowAttachment?.locked(this.$access, this.$store);
    },
    showPlay() {
      if (this.rowAttachment) {
        return this.rowAttachment.mediaType !== 'image' && this.rowAttachment.mediaType !== 'biodigital';
      }
    },
    timeRemaining() {
      if (this.attachmentState) {
        return this.rowAttachment.metadata?.duration - this.attachmentState.currentTimestamp;
      }
    },
    thumbnail() {
      let url;
      if (this.card) {
        url = this.$store.getters['attachments/get'][this.card.featuredImageId]?.smallThumbnail;
      } else {
        url = this.displayAttachment?.smallThumbnail;
      }

      if (url) {
        return `url("${url}")`;
      }
    },
    user() {
      return this.$store.getters['session/currentUser'];
    },
  },
  methods: {
    formatDuration(duration) {
      return Attachment.formatDuration(duration);
    },
    rowClick() {
      const param = this.card ? this.card : this.rowAttachment;
      this.$emit('row-clicked', param);
    },
  },
};
</script>

<style lang="scss" scoped>
.row {
  background-color: inherit;
  font-weight: $semi-bold;

  img {
    height: rem-calc(16);
    width: rem-calc(16);
  }

  .name {
    align-items: center;
    color: #000000;
    display: flex;
    flex: 1;
    font-size: rem-calc(14);
    margin-left: rem-calc(24);
  }

  .thumbnail {
    align-items: center;
    background-color: #ebedef;
    display: flex;
    flex-shrink: 0;
    height: 39px;
    justify-content: center;
    margin-left: rem-calc(24);
    position: relative;
    width: 70px;

    &.thumbnail-img {
      background-position: center;
      background-size: cover;
    }
  }
  .action-container {
    display: flex;
    align-items: center;
    flex-direction: row;
    min-width: rem-calc(60);

    .view {
      margin-left: auto;
      text-align: center;

      img {
        height: rem-calc(20);
        width: rem-calc(20);
      }
    }

    .duration {
      font-size: rem-calc(14);
      text-align: right;
      color: #a2a2a5;
      margin-right: rem-calc(16);

      &.in-progress {
        font-weight: $semi-bold;
        color: #000000;
      }
    }

    @include breakpoint(small) {
      flex-direction: column-reverse;
      min-width: rem-calc(40);

      .view {
        margin-left: 0;

        img {
          margin-bottom: rem-calc(5)
        }
      }

      .duration {
        margin-right: 0;

        /deep/ .remaining-text {
          display: none;
        }
      }
    }
  }
}
</style>
